// Lightweight frontend debug logger controlled by localStorage 'debug:modules'
// Usage: import { dbg, enableModule, disableModule, isEnabled } from '@/utils/debugLogger'
// dbg('scoring', 'message', obj)

type ModuleSet = Record<string, boolean>;

const STORAGE_KEY = 'debug:modules';

function readModules(): ModuleSet {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) || '';
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) return parsed;
  } catch (e) {
    // ignore
  }
  return {};
}

function writeModules(mods: ModuleSet) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mods));
  } catch (e) {
    // ignore
  }
}

export function isEnabled(moduleName: string) {
  const mods = readModules();
  if (mods['*']) return true;
  return !!mods[moduleName];
}

export function enableModule(moduleName: string) {
  const mods = readModules();
  mods[moduleName] = true;
  writeModules(mods);
}

export function disableModule(moduleName: string) {
  const mods = readModules();
  delete mods[moduleName];
  writeModules(mods);
}

export function dbg(moduleName: string, ...args: any[]) {
  if (!isEnabled(moduleName)) return;
  // keep prefix to match backend style
  // eslint-disable-next-line no-console
  const prefix = `[${moduleName.toUpperCase()}]`;
  console.debug(prefix, ...args);

  // forward to backend ingestion when frontend-log or global '*' is enabled
  try {
    if (typeof window !== 'undefined' && (isEnabled('frontend-log') || isEnabled('*'))) {
      const base = (import.meta.env.VITE_API_BASE_URL as string) || '';
      // fire-and-forget
      fetch(base + '/api/logs/frontend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ level: 'debug', message: prefix, meta: args })
      }).catch(() => {});
    }
  } catch (e) {
    // ignore any errors while attempting to post logs
  }
}

export function getAllModules(): Record<string, boolean> {
  return readModules();
}

export default { isEnabled, enableModule, disableModule, dbg };
